// File: nodes/IVRNode.jsx
import { Handle, Position } from 'reactflow';
import {
  Box,
  Flex,
  Text,
  IconButton,
  HStack,
  VStack,
  Tag
} from '@chakra-ui/react';
import { FaPhone, FaClock, FaBolt } from 'react-icons/fa';
import { FiCopy, FiEdit, FiTrash2 } from 'react-icons/fi';
import { LuFileText } from 'react-icons/lu';

const IVRNode = ({ data }) => {
  const hasRetries = data.enableRetries || false;
  const varCount = Object.keys(data.ivrVariableMappings || {}).length;

  return (
    <Box
      bg="white"
      border="2px solid"
      borderColor="var(--brand-primary)"
      borderRadius="xl"
      boxShadow={data.selected ? 'xl' : 'lg'}
      minW="240px"
      position="relative"
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: 'black' }}
      />

      {/* Header */}
      <Flex
        bg="var(--brand-primary)"
        color="white"
        px={4}
        py={2.5}
        borderTopRadius="xl"
        align="center"
        gap={2}
      >
        <FaPhone size={16} />
        <Text noOfLines={1} flex={1} fontSize="sm" fontWeight="medium">
          IVR Call
        </Text>
        <HStack spacing={1}>
          <IconButton
            icon={<FiEdit size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => { e.stopPropagation(); data.onEdit?.(data.id); }}
            aria-label="Edit"
          />
          <IconButton
            icon={<FiCopy size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}
            aria-label="Duplicate"
          />
          <IconButton
            icon={<FiTrash2 size={14} />}
            size="xs"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            onClick={(e) => { e.stopPropagation(); data.onDelete?.(data.id); }}
            aria-label="Delete"
          />
        </HStack>
      </Flex>

      {/* Body */}
      <Box p={3.5}>
        <Text fontSize="sm" fontWeight="medium" mb={1.5}>
          {data.label || 'IVR Call'}
        </Text>

        {/* Template pill */}
        {data.templateName ? (
          <Flex
            align="center"
            gap={1.5}
            bg="orange.50"
            borderRadius="md"
            px={2}
            py={1}
            border="1px solid"
            borderColor="orange.200"
            mb={2}
          >
            <Box color="orange.600">
              <LuFileText size={12} />
            </Box>
            {/* minW=0 lets noOfLines actually truncate. Without it the Text holds its
                full intrinsic width and compresses the tag instead. */}
            <Text fontSize="xs" fontWeight="medium" color="orange.700" noOfLines={1} minW={0}>
              {data.templateName}
            </Text>
            {varCount > 0 && (
              <Tag
                ml="auto"
                size="sm"
                variant="subtle"
                // Belongs to the amber pill it sits inside. It was solid pink — the only
                // pink anywhere in the flow builder, and a direct clash on a non-blue
                // brand build (it read as magenta against the STC purple header).
                bg="orange.100"
                color="orange.800"
                // The tag was being squeezed by the Text above and wrapping "4 vars"
                // onto two lines, which made the pill taller than its own row.
                flexShrink={0}
                whiteSpace="nowrap"
              >
                {varCount} var{varCount !== 1 ? 's' : ''}
              </Tag>
            )}
          </Flex>
        ) : (
          <Flex
            align="center"
            gap={1.5}
            bg="gray.50"
            borderRadius="md"
            px={2}
            py={1}
            border="1px dashed"
            borderColor="gray.300"
            mb={2}
          >
            <Text fontSize="xs" color="gray.400">No template selected</Text>
          </Flex>
        )}

        {hasRetries && (
          <VStack spacing={1.5} align="stretch">
            <Flex
              align="center"
              gap={1.5}
              fontSize="xs"
              bg="blue.50"
              borderRadius="md"
              px={2}
              py={1}
              border="1px solid"
              borderColor="blue.200"
            >
              <Box color="blue.700"><FaBolt size={12} /></Box>
              <Text color="blue.700" fontWeight="medium">
                {data.noOfRetries || 3} retr{(data.noOfRetries || 3) === 1 ? 'y' : 'ies'}
              </Text>
            </Flex>

            {data.waitTimeBetweenRetries && (
              <Flex
                align="center"
                gap={1.5}
                fontSize="xs"
                bg="purple.50"
                borderRadius="md"
                px={2}
                py={1}
                border="1px solid"
                borderColor="purple.200"
              >
                <Box color="purple.700"><FaClock size={12} /></Box>
                <Text color="purple.700" fontWeight="medium">
                  Wait: {data.waitTimeBetweenRetries} {data.waitTimeUnit || 'minutes'}
                </Text>
              </Flex>
            )}
          </VStack>
        )}
      </Box>

      <Handle
        type="source"
        position={Position.Right}
        style={{ background: 'black' }}
      />
    </Box>
  );
};

export default IVRNode;